<template>
  <ModalBase :state="state" :open="open" :close="closeModal">
    <template #content>
      <div
        class="relative flex flex-col overflow-hidden pb-96 max-h-[87vh]"
      >
        <div
          v-if="submitLoading"
          class="absolute inset-0 z-50 backdrop-blur-md flex flex-col items-center justify-center gap-8"
        >
          <BaseLoader size="50px" />
          <h3 class="text-headline-3 text-neutral-70">
            {{ t('loading') }}
          </h3>
        </div>

        <div class="flex justify-between items-center p-16">
          <p class="text-paragraph-1 whitespace-pre-line">
            {{ t('filledDataAlerts') }}
          </p>
        </div>

        <div class="flex-1 flex gap-16 w-full overflow-y-scroll p-16">
          <template
            v-for="(justificativa, i) in justificativas"
            :key="i"
          >
            <OInput
              :model-value="justificativa"
              size="md"
              stack-label
              class="w-full showPlaceholderOnStack"
              :auto-size="false"
              :class="{ disabled: formatedAvisos.length === 0 }"
              type="textarea"
              :readonly="formatedAvisos.length === 0"
              :label="t('justification.optional')"
              :placeholder="t('justification.placeholder')"
              @update:model-value="justificativas[i] = $event"
            />
          </template>

          <BaseLoader
            v-if="loading.avisos"
            class="w-full grid place-items-center mt-24"
          />

          <EmptyItem
            v-else-if="!loading.avisos && formatedAvisos.length === 0"
            class="w-full grid place-items-center mt-24"
            :text="t('notFound')"
          />

          <template v-else>
            <div
              v-for="i in formatedAvisos"
              :key="i.template_unificado"
              class="border border-neutral-100/10 rounded-md p-8 relative w-full space-y-4 mt-16"
            >
              <p
                class="text-caps-3 text-neutral-60 bg-white absolute -top-8 px-4"
              >
                {{ i.titulo_separator }}
              </p>

              <q-item
                v-for="(aviso, index) in i.items"
                :key="index"
                :class="{ '!border-alert-warning': !aviso.id }"
                class="border border-neutral-100/10 rounded-md w-full p-16 flex-col"
              >
                <p class="text-headline-2">
                  {{ aviso?.titulo }}
                </p>
                <p class="text-neutral-80 text-paragraph-1">
                  {{ aviso?.descricao }}
                </p>

                <q-tooltip
                  v-if="!aviso.id"
                  v-bind="tooltipProps"
                  class="max-w-[16.25rem] !py-6"
                  :style="{
                    border: `1px solid rgba(var(--alert-warning), 1)`,
                  }"
                >
                  Avisos não salvos.
                  <br />Envie as alterações para salvar
                </q-tooltip>
              </q-item>
            </div>
          </template>
        </div>

        <footer
          class="w-full bg-white absolute bottom-0 left-0 flex justify-end gap-16 p-16 border-t border-neutral-100/10"
        >
          <OButton
            class="bg-primary-pure/10"
            size="lg"
            secondary
            @click="closeModal"
          >
            <p class="text-primary-pure">
              {{ t('btn.toCorrect') }}
            </p>
          </OButton>
          <OButton
            size="lg"
            primary
            :label="t('send')"
            :disabled="disabledSubmit"
            @click="onSubmit"
          />
        </footer>
      </div>
    </template>
  </ModalBase>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseLoader from 'components/Loader/BaseLoader.vue'
import EmptyItem from 'components/Empty/EmptyItem.vue'
import ModalBase from './ModalBase.vue'
import OButton from 'components/Button/OButton.vue'
import OInput from 'components/Input/OInput.vue'
import useModal from 'composables/useModal'
import useTemplateUnificado from 'composables/useTemplateUnificado'

const { t } = useI18n()

const { loading, fetchAvisos, avisos } = useTemplateUnificado()

const modalAtencao = useModal({
  header: { title: t('attentionTitle') },
  style: { width: '50rem' },
  contentClass: '!p-0',
})

const { state, open, close, setState } = modalAtencao

const closeModal = () => {
  close()
}

const submitLoading = ref(false)
const justificativas = ref([''])
const isJustificativaId = ref(null)

const tooltipProps = {
  anchor: 'top middle',
  self: 'bottom middle',
  offset: [5, 5],
}

const onSubmit = () => {
  emit('click:enviar', {
    justificativas: justificativas.value,
    avisos: formatedAvisos.value,
    isJustificativaId: isJustificativaId.value,
  })

  submitLoading.value = true
}

const disabledSubmit = computed(() => loading.value.avisos)

const onFetchAvisos = async () => {
  const templateUnificadoParam =
    state.value.data.templates_por_mes_ids.join(',')

  const responseAvisos = await fetchAvisos({
    template_unificado__in: templateUnificadoParam,
  })

  const firstJustificativa =
    responseAvisos?.at(0)?.justificativa || {}
  justificativas.value = [firstJustificativa.justificativa || '']
  isJustificativaId.value = firstJustificativa.id || null
}

function formatAvisos(avisosArr) {
  const sortedAvisosArr = avisosArr.sort((a, b) => {
    const dateA = new Date(a.data_criacao)
    const dateB = new Date(b.data_criacao)

    return dateB - dateA
  })

  const seen = new Set()
  const uniqueAvisos = sortedAvisosArr.filter(({ key_error }) => {
    if (!key_error || !seen.has(key_error)) {
      if (key_error) seen.add(key_error)
      return true
    }
    return false
  })

  const formatedAvisos = Object.values(
    uniqueAvisos.reduce((acc, item) => {
      const {
        fabricante,
        template_unificado,
        configuracao_coluna,
        template_unificado_titulo,
      } = item

      if (!acc[template_unificado]) {
        const tituloBase = configuracao_coluna?.chave_traducao_label
          ? t(configuracao_coluna.chave_traducao_label)
          : configuracao_coluna?.label || template_unificado_titulo

        const titulo_separator = `${tituloBase} - ${fabricante}`
        acc[template_unificado] = {
          template_unificado,
          titulo_separator,
          data_criacao: Math.max(
            ...uniqueAvisos
              .filter(
                (aviso) =>
                  aviso.template_unificado === template_unificado
              )
              .map((aviso) => new Date(aviso.data_criacao).getTime())
          ),
          items: [],
        }
      }

      acc[template_unificado].items.push(item)

      return acc
    }, {})
  )
  const sortedFormatedAvisos = formatedAvisos.sort(
    (a, b) => b.data_criacao - a.data_criacao
  )
  return sortedFormatedAvisos
}

const formatedAvisos = computed(() => {
  const errors = state.value.data.errors || []
  const formatedErrors = errors.map((e) => ({
    ...e,
    titulo: e.chave_traducao_label
      ? t(e.chave_traducao_label)
      : e.label || e.verbose_name_campo,
    descricao: e.message,
  }))

  const avisosArr = [...avisos.value, ...formatedErrors]
  const avisosArrUnique = avisosArr.filter((obj) => {
    if (obj.erro_validacao) {
      return !avisosArr.some(
        (o) => !o.erro_validacao && o.key_error === obj.key_error
      )
    }
    return true
  })

  return formatAvisos(avisosArrUnique)
})

watch(
  () => state.value.open,
  async (opened) => {
    if (opened) {
      submitLoading.value = false
      justificativas.value = ['']
      isJustificativaId.value = null

      onFetchAvisos()
    }
  }
)

const emit = defineEmits(['click:enviar'])

defineExpose({
  state,
  open,
  close,
  setState,
})
</script>

<style lang="scss" scoped>
:deep(.q-textarea.q-field--labeled .q-field__native) {
  height: 70px !important;
}
</style>
